import { attach, createEvent, createStore, restore, sample } from 'effector';
import { SocketEmitPayload, socket } from '../socket';
import { browserEvents } from '../browser-events';

enum SocketState {
  idle = 'idle',
  connected = 'connected',
  reconnecting = 'reconnecting',
}

export const effectorSocket = (url: string) => {
  const socketController = socket(url);
  let reconnectWindowStartedAt: number | null = null;

  const $connection = createStore<{ current: null | ReturnType<typeof socketController.connect> }>({
    current: null
  });

  const connectFx = attach({
    source: $connection,
    effect: (connection) => {
      if (connection.current) {
        connection.current.removeEventListener('open', handleSocketOpen)
        connection.current.removeEventListener('close', handleSocketClose)
        connection.current.close();
      }

      const newConnection = socketController.connect();

      newConnection.addEventListener("open", handleSocketOpen)
      newConnection.addEventListener("close", handleSocketClose)

      return newConnection
    }
  })

  const handleSocketOpen = () => {
    console.log("SOCKET CONNECTION OPENED")
    reconnectWindowStartedAt = null;
    setState(SocketState.connected)
  }

  const handleSocketClose = () => {
    console.log("SOCKET CLOSED")
    setState(SocketState.reconnecting);

    if (reconnectWindowStartedAt === null) {
      reconnectWindowStartedAt = Date.now();
    }

    const timeSinceFirstAttempt = Date.now() - reconnectWindowStartedAt;

    if (timeSinceFirstAttempt <= 10_000) {
      connectFx();
      console.log("RECONNECT TO SOCKETS...")
    } else {
      console.log("RECONNECT LIMIT REACHED (10s WINDOW)")
    }
  }

  const emit = <T>(payload: SocketEmitPayload<T>) => {
    const connection = $connection.getState();

    return connection.current?.emit(payload)
  }


  sample({
    clock: connectFx.doneData,
    fn: (connection) => ({
      current: connection
    }),
    target: $connection
  });

  const setState = createEvent<SocketState>()
  const $state = restore(setState, SocketState.idle)
  const $connected = $state.map(state => state === SocketState.connected)

  sample({
    source: $state,
    clock: browserEvents.focused,
    filter: state => state !== SocketState.connected && state !== SocketState.idle,
    target: connectFx
  })


  const onConnected = sample({
    clock: $connected,
    filter: Boolean
  })

  const on = <T,>(type: string) => {
    const event = createEvent<{data: T}>();

    socketController.on(type, event);

    return event;
  }

  return {
    connectFx,
    $connection,
    on,
    emit,
    $connected,
    $state,
    onConnected
  }
}